import { useMemo, type CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { migrateDoc } from '@/features/studio/model/doc';
import { compileTheme } from '@/features/studio/model/compile';

/**
 * A live-rendered miniature of a Theme Document — the same compiler as the
 * studio canvas and the community view, so cards always show the real thing
 * (never a stale screenshot). Renders a tiny rail/chat wireframe over the
 * doc's background with its accent + surface treatment.
 */
export function ThemeDocThumbnail({ doc, className }: { doc: unknown; className?: string }) {
  const t = useMemo(() => compileTheme(migrateDoc(doc)), [doc]);

  const bgStyle: CSSProperties = t.backgroundImage
    ? { backgroundImage: `${t.dimOverlay}, url(${t.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: 'linear-gradient(160deg,#1c1c20,#0e0e11)' };

  const card = cn(
    'relative overflow-hidden',
    t.cardClass,
    t.radiusClass === 'rounded-none' ? 'rounded-none' : 'rounded-md',
  );

  return (
    <div className={cn('relative overflow-hidden', className)} style={bgStyle}>
      {t.tintOpacity > 0 && (
        <span
          className="absolute inset-0"
          style={{ background: t.accent, opacity: t.tintOpacity, mixBlendMode: 'soft-light' }}
        />
      )}
      <div className="absolute inset-[8%] grid grid-cols-[28%_1fr] gap-[4%]">
        {/* rail */}
        <div className={card}>
          <span className="absolute inset-x-0 top-0 h-[30%]" style={{ background: `linear-gradient(120deg, ${t.accent}, rgba(0,0,0,0.15))` }} />
          <span className="absolute left-[12%] right-[30%] top-[42%] h-[7%] rounded-full bg-white/60" />
          <span className="absolute left-[12%] right-[45%] top-[58%] h-[6%] rounded-full bg-white/25" />
          <span
            className="absolute left-[12%] right-[38%] top-[72%] h-[6%] rounded-full"
            style={{ background: `${t.accent}66` }}
          />
        </div>
        {/* chat */}
        <div className={card}>
          <span className="absolute left-[6%] top-[12%] h-[8%] w-[8%] rounded-full" style={{ background: t.accent }} />
          <span className="absolute left-[18%] right-[30%] top-[13%] h-[6%] rounded-full bg-white/40" />
          <span className="absolute left-[6%] top-[32%] h-[8%] w-[8%] rounded-full bg-white/30" />
          <span className="absolute left-[18%] right-[45%] top-[33%] h-[6%] rounded-full bg-white/25" />
          <span className="absolute left-[6%] top-[52%] h-[8%] w-[8%] rounded-full bg-white/30" />
          <span className="absolute left-[18%] right-[22%] top-[53%] h-[6%] rounded-full bg-white/25" />
          <span className="absolute inset-x-[6%] bottom-[8%] h-[12%] rounded-md border border-white/15 bg-black/30">
            <span
              className="absolute bottom-[18%] right-[2%] top-[18%] aspect-square rounded-sm"
              style={{ background: t.accent, boxShadow: t.glowShadow }}
            />
          </span>
        </div>
      </div>
    </div>
  );
}
